import React from 'react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// BrandMark — the single place the Cadence icon + wordmark is rendered.
//
// Centralising means the brand decision (Cadence in-app, Stratium on sign-in
// and in attribution only) cannot drift. Future changes land in one file.
//
// Performance: image asset is one of three pre-generated sizes in /public.
// Picking the nearest pre-sized asset keeps the decoded-image memory small
// and lets the browser cache the icon across surfaces.
// ---------------------------------------------------------------------------

export type BrandMarkSize = 'sm' | 'md' | 'lg';

interface BrandMarkProps {
  /** sm: sidebar (28px). md: header / signup (40px). lg: login hero (56px). */
  size?: BrandMarkSize;
  /** Show the "Cadence" wordmark next to the icon. Defaults to true. */
  showWordmark?: boolean;
  /** Show the "by Stratium" line below the wordmark. Use only on auth pages
   *  and the sidebar bottom attribution. Do not repeat elsewhere. */
  showStratiumLine?: boolean;
  /** Override label. Defaults to "Cadence". */
  label?: string;
  /** Accent colour for the wordmark gradient. Defaults to theme primary. */
  accent?: 'primary' | 'light';
}

const ICON_PX: Record<BrandMarkSize, number> = {
  sm: 28,
  md: 40,
  lg: 56,
};

const ICON_SRC: Record<BrandMarkSize, string> = {
  sm: '/favicon-64.png',
  md: '/logo192.png',
  lg: '/logo192.png',
};

const WORDMARK_PX: Record<BrandMarkSize, number> = {
  sm: 18,
  md: 22,
  lg: 28,
};

export function BrandMark({
  size = 'md',
  showWordmark = true,
  showStratiumLine = false,
  label = 'Cadence',
  accent = 'primary',
}: BrandMarkProps) {
  const iconPx = ICON_PX[size];
  const iconSrc = ICON_SRC[size];
  const wordmarkPx = WORDMARK_PX[size];

  return (
    <div className="flex items-center gap-2.5">
      <img
        src={iconSrc}
        alt=""
        aria-hidden
        width={iconPx}
        height={iconPx}
        className="block rounded-md"
      />
      {showWordmark && (
        <div>
          <span
            className={cn(
              'block font-bold leading-none',
              accent === 'light' ? 'text-[#F8FAFC]' : 'text-primary',
            )}
            style={{ fontSize: wordmarkPx, letterSpacing: '-0.02em' }}
          >
            {label}
          </span>
          {showStratiumLine && (
            <span
              className="mt-0.5 block text-[11px] font-medium uppercase text-muted-foreground"
              style={{ letterSpacing: '0.04em' }}
            >
              by Stratium
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default BrandMark;
